export const hexToRgb = (
  hex: string,
): { r: number; g: number; b: number } | null => {
  const cleanHex = hex.replace('#', '')

  if (!/^([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(cleanHex)) {
    return null
  }

  const fullHex =
    cleanHex.length === 3
      ? cleanHex
          .split('')
          .map((char) => char + char)
          .join('')
      : cleanHex

  const r = parseInt(fullHex.substring(0, 2), 16)
  const g = parseInt(fullHex.substring(2, 4), 16)
  const b = parseInt(fullHex.substring(4, 6), 16)

  return { r, g, b }
}

export const getRelativeLuminance = (
  r: number,
  g: number,
  b: number,
): number => {
  const [rNorm, gNorm, bNorm] = [r, g, b].map((val) => {
    const normalized = val / 255
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4)
  })

  return 0.2126 * rNorm + 0.7152 * gNorm + 0.0722 * bNorm
}

export const getContrastTextColor = (
  backgroundColor: string,
): '#000000' | '#FFFFFF' => {
  const rgb = hexToRgb(backgroundColor)

  if (!rgb) {
    return '#FFFFFF'
  }

  const luminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b)

  return luminance > 0.5 ? '#000000' : '#FFFFFF'
}

export const getContrastRatio = (
  color1: string,
  color2: string,
): number | null => {
  const rgb1 = hexToRgb(color1)
  const rgb2 = hexToRgb(color2)

  if (!rgb1 || !rgb2) {
    return null
  }

  const lum1 = getRelativeLuminance(rgb1.r, rgb1.g, rgb1.b)
  const lum2 = getRelativeLuminance(rgb2.r, rgb2.g, rgb2.b)

  const lighter = Math.max(lum1, lum2)
  const darker = Math.min(lum1, lum2)

  return (lighter + 0.05) / (darker + 0.05)
}
